import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import {
  Button,
  Box,
  Typography,
  Alert,
  Snackbar,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Chip,
  TextField,
  InputAdornment,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import GroupIcon from '@mui/icons-material/Group';
import StarIcon from '@mui/icons-material/Star';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import { DataTable } from '../components/DataTable';
import { ProductEquivalencyForm } from '../components/product-equivalencies/ProductEquivalencyForm';
import { MembershipManager } from '../components/product-equivalencies/MembershipManager';
import { productEquivalenciesApi, productEquivalencyCategoriesApi } from '../api/services';
import { usePaginatedQuery } from '../hooks/usePaginatedQuery';
import { useDebounce } from '../hooks/useDebounce';
import { extractApiError } from '../utils/apiErrors';
import type { ProductEquivalency } from '../api/config';

export const ProductEquivalenciesPage: React.FC = () => {
  const [isFormOpen, setIsFormOpen] = React.useState(false);
  const [selectedGroup, setSelectedGroup] = React.useState<ProductEquivalency | undefined>();
  const [membershipGroup, setMembershipGroup] = React.useState<ProductEquivalency | undefined>();
  const [snackbar, setSnackbar] = React.useState<{ message: string; severity: 'error' | 'success' } | null>(null);
  const [categoryFilter, setCategoryFilter] = React.useState('');
  const [searchFilter, setSearchFilter] = React.useState('');
  const queryClient = useQueryClient();

  const { data: categories = [] } = useQuery({
    queryKey: ['product_equivalency_categories'],
    queryFn: productEquivalencyCategoriesApi.getAll,
  });

  const debouncedSearch = useDebounce(searchFilter, 300);

  const peFilters = React.useMemo(() => ({
    ...(categoryFilter ? { product_equivalency_category_id: Number(categoryFilter) } : {}),
    ...(debouncedSearch ? { search: debouncedSearch } : {}),
  }), [categoryFilter, debouncedSearch]);

  const {
    rows: groups,
    rowCount,
    isLoading,
    error,
    paginationModel,
    onPaginationModelChange,
    sortModel,
    onSortModelChange,
    filterModel,
    onFilterModelChange,
  } = usePaginatedQuery({
    queryKey: ['product_equivalencies', categoryFilter, debouncedSearch],
    queryFn: (f, p) => productEquivalenciesApi.getPage(f, p),
    filters: peFilters,
    defaultSort: { field: 'name', sort: 'asc' },
  });

  const createMutation = useMutation({
    mutationFn: productEquivalenciesApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['product_equivalencies'] });
      setSnackbar({ message: 'Product equivalency created successfully', severity: 'success' });
      handleCloseForm();
    },
    onError: (error) => {
      setSnackbar({ message: extractApiError(error, 'Failed to create product equivalency'), severity: 'error' });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<ProductEquivalency> }) =>
      productEquivalenciesApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['product_equivalencies'] });
      setSnackbar({ message: 'Product equivalency updated successfully', severity: 'success' });
      handleCloseForm();
    },
    onError: (error) => {
      setSnackbar({ message: extractApiError(error, 'Failed to update product equivalency'), severity: 'error' });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: productEquivalenciesApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['product_equivalencies'] });
      setSnackbar({ message: 'Product equivalency deleted successfully', severity: 'success' });
    },
    onError: (error) => {
      setSnackbar({ message: extractApiError(error, 'Failed to delete product equivalency'), severity: 'error' });
    },
  });

  const mutationError = createMutation.error || updateMutation.error;


  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 60 },
    {
      field: 'name',
      headerName: 'Generic Name',
      width: 250,
      renderHeader: () => (
        <Tooltip title="This name appears in customer-facing emails and dashboards" arrow>
          <span style={{ cursor: 'help', textDecoration: 'underline dotted' }}>Generic Name</span>
        </Tooltip>
      ),
    },
    {
      field: 'category',
      headerName: 'Category',
      filterable: false,
      width: 150,
      renderCell: (params: GridRenderCellParams<ProductEquivalency>) => {
        const cat = params.row?.product_equivalency_category?.name;
        if (!cat) return null;
        return (
          <Chip
            label={cat}
            size="small"
            color="secondary"
            variant="outlined"
          />
        );
      },
    },
    {
      field: 'members',
      headerName: 'Members',
      filterable: false,
      width: 300,
      sortable: false,
      renderCell: (params: GridRenderCellParams<ProductEquivalency>) => {
        const memberships = params.row?.product_equivalency_memberships || [];
        if (memberships.length === 0) {
          return (
            <Tooltip title="No members assigned. Should this PE be cleaned up?">
              <WarningAmberIcon sx={{ fontSize: 18, color: 'warning.main' }} />
            </Tooltip>
          );
        }
        return (
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
            {memberships.map((m) => {
              const isHexa = m.product?.manufacturer?.name === 'Hexa';
              return (
                <Chip
                  key={m.sku}
                  label={m.sku}
                  size="small"
                  icon={isHexa ? <StarIcon fontSize="small" /> : undefined}
                  color={isHexa ? 'primary' : 'default'}
                  variant={isHexa ? 'filled' : 'outlined'}
                />
              );
            })}
          </Box>
        );
      },
    },
    {
      field: 'hexa_hq_sku',
      width: 150,
      renderHeader: () => (
        <Tooltip title="Used to match PE to a HexaHQ product when generating restock quotes. Unrelated to product-level SKUs and never shown on dashboards or digests." arrow>
          <span style={{ cursor: 'help', textDecoration: 'underline dotted' }}>HexaHQ SKU</span>
        </Tooltip>
      ),
      renderCell: (params: GridRenderCellParams<ProductEquivalency>) => {
        const sku = params.row?.hexa_hq_sku;
        if (!sku?.trim()) {
          return (
            <Tooltip title="No HexaHQ SKU set. Ignore for products with no restockable HEXA equivalent.">
              <WarningAmberIcon sx={{ fontSize: 18, color: 'warning.main' }} />
            </Tooltip>
          );
        }
        return <>{sku}</>;
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 160,
      filterable: false,
      sortable: false,
      renderCell: (params: GridRenderCellParams<ProductEquivalency>) => {
        const group = params.row;
        if (!group) return null;
        return (
          <Box>
            <Tooltip title="Manage Members">
              <IconButton size="small" onClick={() => setMembershipGroup(group)}>
                <GroupIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Edit">
              <IconButton size="small" onClick={() => handleEdit(group)}>
                <EditIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton size="small" onClick={() => handleDelete(group.id)}>
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },
  ];

  const handleCloseForm = () => {
    setIsFormOpen(false);
    createMutation.reset();
    updateMutation.reset();
  };

  const handleSubmit = (data: Pick<ProductEquivalency, 'name' | 'hexa_hq_sku' | 'product_equivalency_category_id' | 'description'>) => {
    createMutation.reset();
    updateMutation.reset();
    if (selectedGroup) {
      updateMutation.mutate({ id: selectedGroup.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleEdit = (group: ProductEquivalency) => {
    setSelectedGroup(group);
    setIsFormOpen(true);
  };

  const handleDelete = (id: number) => {
    if (window.confirm('Are you sure you want to delete this product equivalency?')) {
      deleteMutation.mutate(id);
    }
  };

  const handleNavigateToGroup = React.useCallback((groupId: number) => {
    const existingGroup = groups.find((g) => g.id === groupId);
    if (existingGroup) {
      handleCloseForm();
      setMembershipGroup(existingGroup);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups]);

  const handleCategoryFilterChange = (e: SelectChangeEvent<string>) => {
    setCategoryFilter(e.target.value);
  };

  // Search is now server-side (debounced, matches name and hexa_hq_sku)

  if (error) {
    return <Alert severity="error">Failed to load product equivalencies</Alert>;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Product Equivalencies</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setIsFormOpen(true)}
        >
          Create Equivalency
        </Button>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <TextField
          size="small"
          placeholder="Search…"
          value={searchFilter}
          onChange={(e) => setSearchFilter(e.target.value)}
          sx={{ minWidth: 300 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
            endAdornment: searchFilter ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchFilter('')}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Category</InputLabel>
          <Select
            value={categoryFilter}
            onChange={handleCategoryFilterChange}
            label="Category"
          >
            <MenuItem value="">All Categories</MenuItem>
            {categories.map((cat) => (
              <MenuItem key={cat.id} value={String(cat.id)}>{cat.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <DataTable
        rows={groups}
        columns={columns}
        loading={isLoading}
        getRowId={(row) => row.id}
        pageSizeOptions={[25, 50, 100]}
        fullHeight
        paginationMode="server"
        sortingMode="server"
        rowCount={rowCount}
        paginationModel={paginationModel}
        onPaginationModelChange={onPaginationModelChange}
        sortModel={sortModel}
        onSortModelChange={onSortModelChange}
        filterModel={filterModel}
        onFilterModelChange={onFilterModelChange}
      />

      <ProductEquivalencyForm
        open={isFormOpen}
        onClose={handleCloseForm}
        onSubmit={handleSubmit}
        initialData={selectedGroup}
        onExited={() => setSelectedGroup(undefined)}
        error={mutationError ? extractApiError(mutationError) : undefined}
        isPending={createMutation.isPending || updateMutation.isPending}
        onNavigateToGroup={handleNavigateToGroup}
      />

      {membershipGroup && (
        <MembershipManager
          open={true}
          onClose={() => setMembershipGroup(undefined)}
          group={membershipGroup}
        />
      )}

      {snackbar && (
        <Snackbar
          open={true}
          autoHideDuration={6000}
          onClose={() => setSnackbar(null)}
        >
          <Alert severity={snackbar.severity} onClose={() => setSnackbar(null)}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      )}
    </Box>
  );
};
